import React, { useCallback } from 'react';
import { SectionEventContentDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';
import { IkImage } from 'ui-components/Imagekit/IkImage';
import { getValidContentAndProfile } from 'utils/sectionContentUtils';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import { SectionEventContentItem } from 'app-components/home/sections/items/SectionEventContentItem';

interface SectionEventContentProps {
  section: SectionEventContentDto;
  contents: Record<number, ContentRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
}

export const SectionEventContent = ({
  section,
  contents,
  profileLites,
  trackerExtraInfo,
}: SectionEventContentProps & TrackerExtraInfoProps) => {
  const { tracker } = useTrackingState();
  const blockedProfileIds = useBlockProfiles();

  const handleOnClickTracker = useCallback(
    (contentId: number) => {
      tracker.track(TrackingEventType.SelectEventContentItem, {
        sectionId: section.id,
        sectionTitle: section.title,
        contentId,
        trackerExtraInfo,
      });
    },
    [section.id, section.title, tracker, trackerExtraInfo],
  );

  const renderSubTitle = useCallback(
    () => (
      <div className="flex justify-between items-center relative px-4 overflow-hidden w-full h-16 bg-black">
        <div className="flex justify-between items-center gap-1 z-[1] w-full">
          <div className="flex justify-center items-center">
            <IkImage
              height={20}
              transform={{
                path: section.titleFile.path,
                isPrivate: section.titleFile.isPrivate,
              }}
            />
          </div>
          {section.description && (
            <div className="w-[230px] text-xxxs font-regular text-gray-100 text-right">{section.description}</div>
          )}
        </div>
        <div className="absolute w-[150%] aspect-[425/637] left-[-25%] blur-[54px] [transform:translate3d(0,0,0)] bg-black/10">
          <IkImage
            fullWidth
            transform={{
              path: section.bannerFile.path,
              isPrivate: section.bannerFile.isPrivate,
            }}
          />
          <div className="absolute inset-0 bg-black/10" />
        </div>
      </div>
    ),
    [section],
  );

  const renderTopDivider = useCallback(
    () => (
      <div className="flex justify-center w-full h-[1px] px-4">
        <div className="w-full bg-black/10" />
      </div>
    ),
    [],
  );

  if (section.contentIds.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <div className="relative flex-col justify-start items-start mb-4 mx-4 rounded-md overflow-hidden">
        <div className="absolute inset-0 rounded-md z-[2] pointer-events-none border border-solid border-black/10" />
        {renderSubTitle()}
        {section.contentIds.map((contentId, index) => {
          const validationResult = getValidContentAndProfile(contentId, contents, profileLites, blockedProfileIds);
          if (!validationResult) {
            return null;
          }

          const { content, profile } = validationResult;

          return (
            <div key={`vertical-${content.id}-${index}`} className="w-full">
              {index > 0 && renderTopDivider()}
              <SectionEventContentItem
                key={contentId}
                content={content}
                profile={profile}
                onClick={handleOnClickTracker}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
